import { useEffect, useRef, useState } from 'react';

interface Props {
  flowName: string;
  saved: boolean;
  saving: boolean;
  readOnly: boolean;
  stateLabel: string;
  canEnterDraft: boolean;
  // When true (legacy draft/commit lifecycle) the Discard/Publish buttons
  // are rendered. The aivrmanager port runs without drafts (writes are live)
  // so it leaves this off and skips both buttons entirely.
  showCommitControls?: boolean;
  onFit: () => void;
  onReorder: () => void;
  onValidate: () => void;
  onExport: () => void;
  onPublish: () => void;
  onDiscard: () => void;
  onEnterDraft: () => void;
  onBack: () => void;
}

// Mirrors the POC topbar. The flow name is read-only here because renaming
// the flow happens through the existing FlowDetails screen; we just display it.
// The Publish/Discard buttons map to FlowCommit/FlowDiscard so the visual
// builder participates in the same draft/publish lifecycle as the form view.
export function Topbar({
  flowName,
  saved,
  saving,
  readOnly,
  stateLabel,
  canEnterDraft,
  showCommitControls = false,
  onFit,
  onReorder,
  onValidate,
  onExport,
  onPublish,
  onDiscard,
  onEnterDraft,
  onBack,
}: Props) {
  // Phones: the action buttons live in a "⋯" menu instead of the bar.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);
  const pick = (fn: () => void) => () => {
    setMenuOpen(false);
    fn();
  };

  let dot = 'fb-dot fb-dot--unsaved';
  let txt = 'unsaved';
  if (readOnly) {
    dot = 'fb-dot fb-dot--readonly';
    txt = 'read-only';
  } else if (saving) {
    dot = 'fb-dot fb-dot--unsaved';
    txt = 'saving…';
  } else if (saved) {
    dot = 'fb-dot fb-dot--saved';
    txt = 'saved';
  }

  return (
    <div className="fb-topbar">
      <span className="fb-logo">
        Flow<em>Builder</em>
      </span>
      <span className="fb-divider-v" />
      <span className="fb-flow-name" title={flowName}>{flowName}</span>
      <span className={`fb-state-badge fb-state-badge--${stateLabel.toLowerCase()}`} title={`Flow state: ${stateLabel}`}>
        {stateLabel}
      </span>
      <span className="fb-status">
        <span className={dot} />
        <span>{txt}</span>
      </span>
      <span className="fb-spacer" />
      <div className="fb-tb-actions">
        <button className="fb-btn fb-btn--ghost" onClick={onBack} title="Back to application">
          ← Back
        </button>
        <button className="fb-btn fb-btn--ghost" onClick={onFit} title="Fit all nodes">
          Fit
        </button>
        {!readOnly && (
          <button
            className="fb-btn fb-btn--ghost"
            onClick={onReorder}
            title="Auto-arrange nodes top→down from the start node"
          >
            Reorder
          </button>
        )}
        <button className="fb-btn" onClick={onValidate}>
          Validate
        </button>
        <button className="fb-btn" onClick={onExport}>
          Export JSON
        </button>
        <span className="fb-divider-v" />
      </div>
      <div className="fb-tb-more" ref={menuRef}>
        <button
          type="button"
          className="fb-btn"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="More actions"
          onClick={() => setMenuOpen((o) => !o)}
        >
          ⋯
        </button>
        {menuOpen && (
          <div className="fb-tb-menu" role="menu">
            <button type="button" role="menuitem" onClick={pick(onBack)}>← Back to application</button>
            <button type="button" role="menuitem" onClick={pick(onFit)}>Fit all nodes</button>
            {!readOnly && <button type="button" role="menuitem" onClick={pick(onReorder)}>Reorder</button>}
            <button type="button" role="menuitem" onClick={pick(onValidate)}>Validate</button>
            <button type="button" role="menuitem" onClick={pick(onExport)}>Export JSON</button>
          </div>
        )}
      </div>
      {readOnly && canEnterDraft && (
        <button
          className="fb-btn fb-btn--primary"
          onClick={onEnterDraft}
          title={stateLabel === 'Expired' ? 'Claim this draft' : 'Create a draft to edit'}
        >
          {stateLabel === 'Expired' ? 'Claim' : 'Edit'}
        </button>
      )}
      {!readOnly && showCommitControls && (
        <>
          <button className="fb-btn fb-btn--danger" onClick={onDiscard} title="Discard draft">
            Discard
          </button>
          <button className="fb-btn fb-btn--primary" onClick={onPublish} title="Commit draft">
            Publish
          </button>
        </>
      )}
    </div>
  );
}
